import React, { useContext } from 'react';

import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import DialogActions from '@mui/material/DialogActions';
import IconsSend from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import FormHelperText from '@mui/material/FormHelperText';
import ButtonSubmit from '../../../components/_button/ButtonSubmit';
import useNotification from '../../../components/_hook/useNotification';
import useForm from '../../../components/_hook/useForm';
import { IValue } from '../../../components/_select/_module/Select';
import { usePlanQuery } from '../../../../graphql/query/plan/usePlanQuery';
import { useUpdatePlanPackageMutation } from '../../../../graphql/mutation/plan/updatePlanPackageMutation';
import CircularProgress from '../../../components/_loading/CircularProgress';
import SelectKeepitalSubscriptionPackage from '../../../components/_select/SelectKeepitalSubscriptionPackage';
import NotFound from '../../../components/_error/NotFound';
import Context from '../../../context';

interface IProps {
  onSubmit?: () => void;
  planId: string;
}

export default function FormUpdatePlanPackage(props: IProps) {
  const { t } = useTranslation();
  const { onSubmit, planId } = props;
  const { notification } = useNotification();
  const { isSuperAdmin } = useContext(Context);
  const { data, loading } = usePlanQuery(
    `_id
            status
            company`,
    {
      variables: { filter: { _id: planId as string } },
    }
  );
  const [updatePlanPackageMutation] = useUpdatePlanPackageMutation(
    `updatedPlan {
				_id
				status
				subscription {
				  packageID
				}
			}`,
    {
      onCompleted: () => {
        notification.success(t('success'));
        if (onSubmit) {
          onSubmit();
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const { values, setValue, handleSubmit, errors, isSubmitting } = useForm<{
    billCycle: 'annually' | 'monthly';
    packageId: string;
  }>({
    defaultValues: {
      billCycle: 'annually',
      packageId: '',
    },
    onSubmit: async (tempValues) => {
      if (data && data.plans.items[0]) {
        if (isSuperAdmin) {
          await updatePlanPackageMutation({
            variables: {
              companyId: data.plans.items[0].company,
              billCycle: tempValues.billCycle,
              packageId: tempValues.packageId,
            },
          });
        }
      }
    },
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.billCycle) {
        newErrors.billCycle = t('please_select__item__', {
          item: t('billing_cycle'),
        });
      }
      if (!tempValues.packageId) {
        newErrors.packageId = t('please_select__item__', {
          item: t('package'),
        });
      }

      return newErrors;
    },
  });

  const plan = data ? data.plans.items[0] : null;

  if (loading) return <CircularProgress center color="primary" />;
  if (!plan) return <NotFound />;

  return (
    <>
      <DialogTitle>
        {t('update')} {t('plan')} {t('package')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>{t('billing_cycle')}</FormLabel>
              <RadioGroup
                row
                name="billCycle"
                value={values.billCycle}
                onChange={(e) => setValue('billCycle', e.target.value)}
              >
                <FormControlLabel
                  value="monthly"
                  control={<Radio color="primary" />}
                  label={t('monthly')}
                />
                <FormControlLabel
                  value="annually"
                  control={<Radio color="primary" />}
                  label={t('annually')}
                />
              </RadioGroup>
              <FormHelperText error>{errors.billCycle}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <SelectKeepitalSubscriptionPackage<IValue>
              required
              menuPosition="fixed"
              error={Boolean(errors.packageId)}
              helperText={errors.packageId}
              label={t('package')}
              fullWidth
              placeholder={t('package')}
              value={values.packageId}
              onChange={(option) => {
                setValue('packageId', option ? option.value : '');
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonSubmit
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          isSubmitting={isSubmitting}
          label={t('submit')}
          icon={<IconsSend />}
        />
      </DialogActions>
    </>
  );
}
